import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnInit,
} from '@angular/core';
import { SITE_NAME } from '@freelancer/config';
import { Datastore, DatastoreDocument } from '@freelancer/datastore';
import {
  ProfileViewUsersCollection,
  SimilarFreelancersCollection,
  SimilarTag,
} from '@freelancer/datastore/collections';
import { FontType, TextTransform } from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { map, startWith } from 'rxjs/operators';

interface SkillCountryTag {
  readonly skillTag: SimilarTag | undefined;
  readonly countryTag: SimilarTag | undefined;
}

@Component({
  selector: 'app-user-profile-breadcrumbs',
  template: `
    <fl-breadcrumbs [flHideMobile]="true">
      <fl-breadcrumbs-item link="/" flTrackingLabel="ProfileBreadcrumbSiteName">
        {{ siteName }}
      </fl-breadcrumbs-item>

      <fl-breadcrumbs-item
        *ngIf="skillTag$ | flAsync as skillTag"
        flTrackingLabel="ProfileBreadcrumbSkill"
        [link]="skillTag.url"
      >
        {{ skillTag.label }}
      </fl-breadcrumbs-item>

      <fl-breadcrumbs-item
        *ngIf="countryTag$ | flAsync as countryTag"
        flTrackingLabel="ProfileBreadcrumbCountry"
        [link]="countryTag.url"
      >
        {{ countryTag.label }}
      </fl-breadcrumbs-item>

      <fl-breadcrumbs-item
        *ngIf="!(countryTag$ | flAsync) && !(skillTag$ | flAsync)"
        flTrackingLabel="ProfileBreadcrumbDefault"
        i18n="Freelancers for hire label"
        link="/freelancers"
      >
        Freelancers for Hire
      </fl-breadcrumbs-item>

      <fl-breadcrumbs-item
        *ngIf="profileDisplayName$ | flAsync as profileDisplayName"
      >
        {{ profileDisplayName | uppercase }}
      </fl-breadcrumbs-item>
    </fl-breadcrumbs>

    <fl-text
      *ngIf="skillCountryTag$ | flAsync as skillCountryTag"
      [flShowMobile]="true"
      [fontType]="FontType.CONTAINER"
      [textTransform]="TextTransform.UPPERCASE"
    >
      <fl-link
        *ngIf="skillCountryTag.countryTag && skillCountryTag.skillTag"
        flTrackingLabel="ProfileBreadcrumbFullMobile"
        i18n="See the freelancer directory for this country and skill"
        [iconName]="'ui-arrow-left'"
        [link]="skillCountryTag.countryTag.url"
      >
        See all
        {{ skillCountryTag.skillTag.label }}
        in
        {{ skillCountryTag.countryTag.label }}
      </fl-link>

      <fl-link
        *ngIf="!skillCountryTag.countryTag && skillCountryTag.skillTag"
        flTrackingLabel="ProfileBreadcrumbSkillOnlyMobile"
        i18n="See the freelancer directory for this skill"
        [iconName]="'ui-arrow-left'"
        [link]="skillCountryTag.skillTag.url"
      >
        See all {{ skillCountryTag.skillTag.label }}
      </fl-link>

      <fl-link
        *ngIf="skillCountryTag.countryTag && !skillCountryTag.skillTag"
        flTrackingLabel="ProfileBreadcrumbCountryOnlyMobile"
        i18n="See the freelancer directory for this country"
        [iconName]="'ui-arrow-left'"
        [link]="skillCountryTag.countryTag.url"
      >
        See all Freelancers in
        {{ skillCountryTag.countryTag.label }}
      </fl-link>

      <fl-link
        *ngIf="!skillCountryTag.countryTag && !skillCountryTag.skillTag"
        flTrackingLabel="ProfileBreadcrumbDefaultMobile"
        i18n="See the freelancer directory"
        link="/freelancers"
        [iconName]="'ui-arrow-left'"
      >
        See all Freelancers for Hire
      </fl-link>
    </fl-text>
  `,
  styleUrls: ['./user-profile-breadcrumbs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileBreadcrumbsComponent implements OnInit {
  FontType = FontType;
  TextTransform = TextTransform;

  @Input() profileUserId$: Rx.Observable<number>;
  @Input()
  profileViewUsersDocument: DatastoreDocument<ProfileViewUsersCollection>;
  @Input() profileDisplayName$: Rx.Observable<string>;

  countryTag$: Rx.Observable<SimilarTag | undefined>;
  skillTag$: Rx.Observable<SimilarTag | undefined>;
  skillCountryTag$: Rx.Observable<SkillCountryTag>;

  constructor(
    @Inject(SITE_NAME) public siteName: string,
    private datastore: Datastore,
  ) {}

  ngOnInit(): void {
    const similarFreelancersDocument = this.datastore.document<SimilarFreelancersCollection>(
      'similarFreelancers',
      this.profileUserId$,
    );

    const skillCountryUrl$ = similarFreelancersDocument
      .valueChanges()
      .pipe(
        map(similarFreelancers =>
          similarFreelancers.tags.length > 0
            ? similarFreelancers.tags[0].url
            : undefined,
        ),
      );

    this.countryTag$ = Rx.combineLatest([
      this.profileViewUsersDocument.valueChanges(),
      skillCountryUrl$.pipe(startWith(undefined)),
    ]).pipe(
      map(([user, skillCountryUrl]) => {
        if (
          !user.location ||
          !user.location.country ||
          user.location.country.name === ''
        ) {
          return undefined;
        }

        const countryName = user.location.country.name;
        // This replicates the directory seo url found in
        // \DirectoryUtil::getSeoUrlFromString
        const seoCountryName = countryName
          .toLowerCase()
          .replace(/[^a-zA-Z ]/g, '')
          .replace(/[^a-zA-Z]/g, '-');

        return {
          label: countryName,
          url: skillCountryUrl || `/freelancers/${seoCountryName}`,
        };
      }),
    );

    // If the user has a country and skill the first item in this array will always be
    // `<skill> in <country>`. We only want to be showing `skill`, this is always
    // the next item in the array. This is why we are selecting the second
    // array element if the user has a country and the first if they do not.
    this.skillTag$ = Rx.combineLatest([
      similarFreelancersDocument.valueChanges(),
      this.countryTag$.pipe(startWith(undefined)),
    ]).pipe(
      map(([similarFreelancers, countryTag]) =>
        countryTag ? similarFreelancers.tags[1] : similarFreelancers.tags[0],
      ),
    );

    this.skillCountryTag$ = Rx.combineLatest([
      this.countryTag$,
      this.skillTag$,
    ]).pipe(
      map(([countryTag, skillTag]) => ({
        skillTag,
        countryTag,
      })),
    );
  }
}
